import React, { useState } from 'react';
import { Clock, AlertTriangle, MessageSquare, Copy, Check, ChevronRight } from 'lucide-react';
import UserAvatar from '../UserAvatar';
// Assumindo que as constantes e utilitários estão nesses caminhos
import { PROJECT_STATUSES } from '../../../constants/appConstants';
import { copyToClipboard } from '../../../utils/clipboard';

/**
 * Componente de card para exibir um projeto.
 * @param {object} props - Propriedades do componente.
 * @param {object} props.project - O objeto do projeto.
 * @param {function} props.onCardClick - Função a ser executada ao clicar no card.
 * @returns {React.Component} O componente do card de projeto.
 */
const ProjectCard = ({ project, onCardClick }) => {
    // Estado para controlar o feedback visual de cópia do protocolo
    const [copySuccess, setCopySuccess] = useState(false);

    /**
     * Lida com o clique no botão de copiar protocolo.
     * @param {React.MouseEvent} e - O evento de clique.
     */
    const handleCopyProtocol = async (e) => {
        e.stopPropagation(); // Impede que o clique no botão propague para o card e abra o modal
        const success = await copyToClipboard(project.protocolo);
        if (success) {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000); // Reseta o ícone após 2 segundos
        }
    };

    // Encontra as informações de status (label, cor) com base no status do projeto
    const statusInfo = PROJECT_STATUSES.find(s => s.value.toLowerCase() === (project.status || '').toLowerCase()) || {
        label: project.status || 'Desconhecido',
        tagClasses: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200' // Estilo de fallback
    };

    // Calcula a tag de status de prazo (Atrasado, Atenção) diretamente durante o render
    const getStatusTag = () => {
        // Se o projeto já foi aprovado ou não tem data de vencimento, não exibe a tag
        if (project.status === 'Aprovado' || !project.dueDate) {
            return null;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0); // Zera a hora para comparar apenas a data
        // Converte a data de vencimento, considerando o fuso horário para evitar erros de um dia
        const dueDate = new Date(project.dueDate + 'T00:00:00-03:00'); 
        const diffTime = dueDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return {
                text: 'Atrasado',
                icon: <Clock className="h-3 w-3 mr-1"/>,
                className: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
            };
        } else if (diffDays <= 15) {
            return {
                text: 'Atenção!',
                icon: <AlertTriangle className="h-3 w-3 mr-1"/>,
                className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300'
            };
        }
        return null;
    };

    const statusTag = getStatusTag();

    const handleKeyDown = (e) => {
        // Ignora se o evento se originou de um elemento interativo interno (como o botão de copiar)
        if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onCardClick(project);
        }
    };

    const ariaLabel = `Projeto ${project.protocolo}, ${project.cidade} ${project.local ? project.local : ''}, ${project.totalPostes} postes. Status: ${statusInfo.label}. ${statusTag ? statusTag.text : ''}.`;

    return (
        <div
            onClick={() => onCardClick(project)}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
            aria-label={ariaLabel}
            className="group bg-slate-50 dark:bg-slate-800/60 p-4 rounded-lg border border-slate-200 dark:border-slate-700 flex justify-between items-center cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
        >
            {/* Lado Esquerdo: Informações do Projeto */}
            <div>
                <div className="flex items-center gap-2">
                    <div className="flex items-center">
                        <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/50 py-1 px-2 rounded">
                            {project.protocolo}
                        </span>
                        <button 
                            type="button"
                            onClick={handleCopyProtocol} 
                            title={copySuccess ? "Protocolo copiado!" : "Copiar Protocolo"}
                            aria-label={copySuccess ? "Protocolo copiado!" : "Copiar Protocolo"}
                            className="p-1 rounded-full text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-slate-900"
                        >
                            {copySuccess ? (
                                <Check className="h-4 w-4 text-green-500" />
                            ) : (
                                <Copy className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    {project.cidade} {project.local && `(${project.local})`} - {project.totalPostes} postes
                </p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {/* Tags */}
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusInfo.tagClasses}`}>
                        {statusInfo.label}
                    </span>
                    {statusTag && (
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full flex items-center ${statusTag.className}`}>
                            {statusTag.icon} {statusTag.text}
                        </span>
                    )}
                    {(project.comments && project.comments.length > 0) && (
                        <span className="text-xs font-semibold px-2 py-1 rounded-full flex items-center bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                            <MessageSquare className="h-3 w-3 mr-1"/> {project.comments.length}
                        </span>
                    )}
                </div>
            </div>

            {/* Lado Direito: Thumbnails e Seta */}
            <div className="flex items-center">
                {/* Thumbnails dos Visualizadores */}
                {project.viewers && project.viewers.length > 0 && (
                    <div
                        className="flex items-center"
                        title={`Visualizado por: ${project.viewers.map(v => v.displayName).join(', ')}`}
                    >
                        {project.viewers.slice(0, 5).map((viewer, index) => (
                            <div
                                key={viewer.uid || index}
                                style={{
                                    marginLeft: index > 0 ? '-10px' : '0',
                                    zIndex: project.viewers.length - index,
                                }}
                            >
                                <UserAvatar user={viewer} size="h-6 w-6" />
                            </div>
                        ))}
                    </div>
                )}

                {/* Seta de Detalhes */}
                <div className="ml-4 flex-shrink-0">
                    <div className="p-2 rounded-full text-slate-500 dark:text-slate-400 group-hover:bg-slate-100 dark:group-hover:bg-slate-700 transition-colors">
                        <ChevronRight size={24} />
                    </div>
                </div>
            </div>
        </div>
    );
};

// Memoized with custom comparison to prevent re-renders when parent (ProjectsPage) filters/searches
// or when useAppData creates new project object references without actual data changes.
const projectAreEqual = (prevProps, nextProps) => {
    if (prevProps.onCardClick !== nextProps.onCardClick) return false;

    const p1 = prevProps.project;
    const p2 = nextProps.project;

    if (!p1 || !p2) return p1 === p2;
    if (p1.id !== p2.id) return false;

    // Quick check by updatedAt timestamp if available
    if (p1.updatedAt !== p2.updatedAt) return false;

    // Fallback: shallow compare rendered fields
    if (p1.status !== p2.status) return false;
    if (p1.cidade !== p2.cidade) return false;
    if (p1.local !== p2.local) return false;
    if (p1.totalPostes !== p2.totalPostes) return false;
    if (p1.dueDate !== p2.dueDate) return false;
    if (p1.protocolo !== p2.protocolo) return false;

    // Comments: only length matters for the card counter
    const c1Length = p1.comments ? p1.comments.length : 0;
    const c2Length = p2.comments ? p2.comments.length : 0;
    if (c1Length !== c2Length) return false;

    // Viewers: check length and UIDs to reflect avatar changes
    const v1 = p1.viewers || [];
    const v2 = p2.viewers || [];
    if (v1.length !== v2.length) return false;
    for (let i = 0; i < v1.length; i++) {
        if (v1[i]?.uid !== v2[i]?.uid) return false;
    }

    return true;
};

export default React.memo(ProjectCard, projectAreEqual);